import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button } from "@/components/ui/button";
import { AiOutlineEdit, AiOutlineClose } from 'react-icons/ai';
import { BsList } from 'react-icons/bs';
import { FiType, FiAlignLeft, FiMail, FiPaperclip, FiFileText, FiList } from 'react-icons/fi';
import { componentTypes } from './constants';

const ComponentList = ({ 
  components, 
  onDragEnd, 
  onEditComponent, 
  onRemoveComponent,
  formErrors
}) => {
  const getComponentIcon = (type) => {
    const iconMap = {
      text: <FiType className="h-4 w-4" />,
      textarea: <FiAlignLeft className="h-4 w-4" />,
      email: <FiMail className="h-4 w-4" />,
      file: <FiPaperclip className="h-4 w-4" />,
      markdown: <FiFileText className="h-4 w-4" />,
      select: <FiList className="h-4 w-4" />
    };
    
    return iconMap[type] || <FiType className="h-4 w-4" />;
  };

  const getComponentTypeName = (type) => {
    const component = componentTypes.find(c => c.value === type);
    return component ? component.label : type;
  };

  return (
    <>
      {components.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="components">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={`space-y-3 p-2 ${
                  snapshot.isDraggingOver ? 'bg-muted/30 rounded-lg' : ''
                }`}
              >
                {components.map((component, index) => {
                  // Create a stable key based on component properties
                  const stableId = `${component.type}-${component.name || 'markdown'}-${index}`;
                  
                  return (
                    <Draggable key={stableId} draggableId={stableId} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`group bg-card border rounded-lg p-4 transition-all ${
                            snapshot.isDragging 
                              ? 'shadow-lg border-primary/40' 
                              : 'hover:shadow-sm hover:border-border'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div 
                                {...provided.dragHandleProps} 
                                className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
                              >
                                <BsList className="h-5 w-5" />
                              </div>
                              
                              <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-md text-primary">
                                {getComponentIcon(component.type)}
                              </div>
                              
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <h3 className="font-medium">
                                    {component.type === 'markdown' ? 'Markdown Block' : component.name}
                                  </h3>
                                  <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded">
                                    {getComponentTypeName(component.type)}
                                  </span>
                                  {component.required && (
                                    <span className="text-xs px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded">
                                      Required
                                    </span>
                                  )}
                                </div>
                                
                                {/* Simple content preview */}
                                {component.content && (
                                  <p className="text-sm text-muted-foreground mt-1 truncate">
                                    {component.content.length > 60 
                                      ? `${component.content.substring(0, 60)}...` 
                                      : component.content
                                    }
                                  </p>
                                )}
                                
                                {component.placeholder && component.type !== 'markdown' && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Placeholder: {component.placeholder}
                                  </p>
                                )}
                                
                                {component.type === 'select' && component.options && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {component.options.length} option{component.options.length !== 1 ? 's' : ''}: {component.options.slice(0, 2).join(', ')}{component.options.length > 2 ? '...' : ''}
                                  </p>
                                )}
                                
                                {component.type === 'file' && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {component.multipleFiles ? `Up to ${component.maxFiles || 5} files` : 'Single file'}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => onEditComponent(index)}
                                className="h-8 w-8 p-0"
                              >
                                <AiOutlineEdit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => onRemoveComponent(index)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              >
                                <AiOutlineClose className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className="text-center py-8 border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/10">
          <div className="flex flex-col items-center space-y-2">
            <FiList className="h-8 w-8 text-muted-foreground" />
            <h3 className="font-medium">No components yet</h3>
            <p className="text-sm text-muted-foreground">Add your first component to get started</p>
            {formErrors?.components && (
              <p className="text-sm text-destructive mt-2">{formErrors.components}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ComponentList; 